import { useCallback, useEffect, useMemo, useState } from 'react';

import { AxiosError } from 'axios';
import { Clock, Eye, Pause, Play, Plus, Search, Trash2, X } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { FormField, FormSelect } from '@/components/form';
import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useCampaigns, useDeleteCampaign, useUpdateCampaign } from '@/hooks/useCampaigns';
import { useConfigurations } from '@/hooks/useConfigurations';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTelnyxNumbers } from '@/hooks/useTelnyxNumbers';
import { useTwilioNumbers } from '@/hooks/useTwilioNumbers';
import { getEnv } from '@/lib/env';
import { formatDate } from '@/utils/format';

import type { CampaignDetail, CampaignQuery, CampaignStatus } from '@repo/shared-types';

const STATUS_BADGE_STYLES: Record<CampaignStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  FINISHED: 'bg-slate-200 text-slate-800',
  STOPPED: 'bg-rose-100 text-rose-800',
};

const STATUSES: CampaignStatus[] = ['DRAFT', 'ACTIVE', 'FINISHED', 'STOPPED'];

export const CampaignsPage = () => {
  usePageTitle('Campaigns');
  const [searchParams, setSearchParams] = useSearchParams();
  const limit = 10;

  // Read state from URL
  const status = (searchParams.get('status') as CampaignStatus) || '';
  const configurationId = searchParams.get('configuration') || '';
  const page = Number(searchParams.get('page')) || 1;

  // Local state for search input (for debouncing)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');

  // Update URL params helper
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Debounce search input and sync to URL
  useEffect(() => {
    const timer = setTimeout(() => {
      const searchChanged = debouncedSearch !== searchInput;
      setDebouncedSearch(searchInput);
      updateParams({
        search: searchInput || null,
        ...(searchChanged && { page: null }),
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, updateParams, debouncedSearch]);

  // Build query from state
  const query = useMemo(() => {
    const q: Partial<CampaignQuery> = { page, limit };
    if (debouncedSearch) q.search = debouncedSearch;
    if (status) q.status = status;
    if (configurationId) q.configuration_id = configurationId;
    return q;
  }, [debouncedSearch, status, configurationId, page, limit]);

  const { data, isLoading } = useCampaigns(query);
  const campaigns = data?.data ?? [];
  const pagination = data?.pagination;

  const { data: configurations = [] } = useConfigurations();
  const { data: twilioNumbers = [] } = useTwilioNumbers();
  const { data: telnyxNumbers = [] } = useTelnyxNumbers();
  const deleteMutation = useDeleteCampaign();
  const updateMutation = useUpdateCampaign();
  const { toast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState<CampaignDetail | null>(null);
  const [updatingCampaignId, setUpdatingCampaignId] = useState<string | null>(null);
  const [activationTarget, setActivationTarget] = useState<CampaignDetail | null>(null);
  const [activationOutboundNumberId, setActivationOutboundNumberId] = useState('');

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = searchInput || status || configurationId;

  const handleDelete = (campaign: CampaignDetail) => {
    setDeleteTarget(campaign);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({
        variant: 'success',
        title: 'Campaign deleted',
        description: `"${deleteTarget.name}" has been deleted successfully.`,
      });
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete campaign. Please try again.',
      });
    }
  };

  const handleToggleStatus = async (campaign: CampaignDetail) => {
    if (campaign.status !== 'ACTIVE') {
      // A WhatsApp campaign sends a template from its WhatsApp sender — there is
      // no outbound caller ID to choose, so activate straight away.
      if (campaign.channel === 'WHATSAPP') {
        await runActivation(campaign, {});
        return;
      }

      setActivationTarget(campaign);
      const cfg = configurations.find((c) => c.id === campaign.configuration_id) as
        | { call_provider?: string }
        | undefined;
      const initialId =
        cfg?.call_provider === 'telnyx'
          ? ((campaign as { outbound_telnyx_phone_number_id?: string | null })
              .outbound_telnyx_phone_number_id ?? '')
          : (campaign.outbound_twilio_phone_number_id ?? '');
      setActivationOutboundNumberId(initialId);
      return;
    }

    const newStatus: CampaignStatus = 'DRAFT';
    setUpdatingCampaignId(campaign.id);
    try {
      await updateMutation.mutateAsync({
        id: campaign.id,
        data: { status: newStatus },
      });
      toast({
        variant: 'success',
        title: 'Campaign disabled',
        description: `"${campaign.name}" has been disabled successfully.`,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof AxiosError
          ? (error.response?.data?.message ?? error.message)
          : error instanceof Error
            ? error.message
            : 'Unknown error';
      console.error('Failed to update campaign status:', errorMessage);
      toast({
        variant: 'destructive',
        title: 'Failed to disable campaign.',
        description: `${
          error instanceof AxiosError && error.response?.data?.message
            ? error.response.data.message
            : 'Please try again.'
        }`,
      });
    } finally {
      setUpdatingCampaignId(null);
    }
  };

  const runActivation = async (
    campaign: CampaignDetail,
    numberPatch: Record<string, string | null>,
  ) => {
    setUpdatingCampaignId(campaign.id);
    try {
      await updateMutation.mutateAsync({
        id: campaign.id,
        data: { status: 'ACTIVE', ...numberPatch },
      });
      toast({
        variant: 'success',
        title: 'Campaign activated',
        description: `"${campaign.name}" has been activated successfully.`,
      });
      setActivationTarget(null);
      setActivationOutboundNumberId('');
    } catch (error: unknown) {
      const errorMessage =
        error instanceof AxiosError
          ? (error.response?.data?.message ?? error.message)
          : error instanceof Error
            ? error.message
            : 'Unknown error';
      console.error('Failed to activate campaign:', errorMessage);
      toast({
        variant: 'destructive',
        title: 'Failed to activate campaign.',
        description:
          error instanceof AxiosError && error.response?.data?.message
            ? error.response.data.message
            : 'Please try again.',
      });
    } finally {
      setUpdatingCampaignId(null);
    }
  };

  const handleConfirmActivate = async () => {
    if (!activationTarget) return;

    const cfg = configurations.find((c) => c.id === activationTarget.configuration_id) as
      | { call_provider?: string }
      | undefined;
    const useTelnyx = cfg?.call_provider === 'telnyx';

    await runActivation(
      activationTarget,
      useTelnyx
        ? {
            outbound_telnyx_phone_number_id: activationOutboundNumberId || null,
            outbound_twilio_phone_number_id: null,
          }
        : {
            outbound_twilio_phone_number_id: activationOutboundNumberId || null,
            outbound_telnyx_phone_number_id: null,
          },
    );
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Campaigns</p>
          <h1 className="text-3xl font-bold tracking-tight">Outbound campaigns overview</h1>
          <p className="text-muted-foreground max-w-3xl">
            Review current initiatives, monitor status (Active, Paused, Finished, Stopped), and
            launch new outreach with the configured AI agent settings.
          </p>
        </div>
        <Button asChild>
          <Link to="/campaigns/new">
            <Plus className="h-4 w-4" />
            Add campaign
          </Link>
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>All campaigns</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div className="min-w-50 flex-1">
              <label className="text-muted-foreground mb-1 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="focus:ring-primary w-full rounded-md border py-2 pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
                />
              </div>
            </div>
            <div className="min-w-37.5">
              <label
                htmlFor="status-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Status
              </label>
              <select
                id="status-filter"
                value={status}
                onChange={(e) => updateParams({ status: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                <option value="">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-50">
              <label
                htmlFor="configuration-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Configuration
              </label>
              <select
                id="configuration-filter"
                value={configurationId}
                onChange={(e) =>
                  updateParams({ configuration: e.target.value || null, page: null })
                }
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                <option value="">All configurations</option>
                {configurations.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.name}
                  </option>
                ))}
              </select>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex min-h-50 items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
                <p className="text-muted-foreground mt-2 text-sm">Loading campaigns...</p>
              </div>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
              No campaigns found. Create your first campaign to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-muted-foreground text-left text-sm">
                    <th className="border-b px-3 py-2">Name</th>
                    <th className="border-b px-3 py-2">Configuration</th>
                    <th className="border-b px-3 py-2">Status</th>
                    <th className="border-b px-3 py-2">Contacts</th>
                    {getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') == 'NEW_CROSS' && (
                      <th className="border-b px-3 py-2">Hours Captured</th>
                    )}
                    <th className="border-b px-3 py-2">Runs at</th>
                    <th className="border-b px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="text-sm">
                      <td className="border-b px-3 py-2 font-medium">{campaign.name}</td>
                      <td className="border-b px-3 py-2">{campaign.configurationName}</td>
                      <td className="border-b px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_BADGE_STYLES[campaign.status]}`}
                        >
                          {campaign.status}
                        </span>
                      </td>
                      <td className="border-b px-3 py-2">
                        {campaign.contacts.length.toLocaleString()}
                      </td>
                      {getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') == 'NEW_CROSS' && (
                        <td className="border-b px-3 py-2">
                          {campaign.totalAvailabilityHours ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
                                <Clock className="h-3.5 w-3.5" />
                                {campaign.totalAvailabilityHours}h
                              </span>
                              {campaign.availabilityHoursSource && (
                                <span
                                  className={
                                    campaign.availabilityHoursSource === 'tool'
                                      ? 'rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                                      : 'bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 text-[10px] font-medium'
                                  }
                                >
                                  {campaign.availabilityHoursSource === 'tool'
                                    ? 'Tool'
                                    : 'Reviewed'}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      )}
                      <td className="border-b px-3 py-2">{formatDate(campaign.runsAt)}</td>
                      <td className="border-b px-3 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="secondary" size="sm" asChild>
                            <Link to={`/campaigns/${campaign.id}`}>
                              <Eye className="mr-1.5 h-4 w-4" />
                              View
                            </Link>
                          </Button>
                          {campaign.status === 'ACTIVE' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleStatus(campaign)}
                              disabled={updatingCampaignId === campaign.id}
                            >
                              <Pause className="mr-1.5 h-4 w-4" />
                              Disable
                            </Button>
                          ) : (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleToggleStatus(campaign)}
                              disabled={
                                updatingCampaignId === campaign.id || campaign.status === 'FINISHED'
                              }
                            >
                              <Play className="mr-1.5 h-4 w-4" />
                              Active
                            </Button>
                          )}
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(campaign)}
                          >
                            <Trash2 className="mr-1.5 h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={(p) => updateParams({ page: p > 1 ? String(p) : null })}
              resourceName="campaigns"
            />
          )}
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        resourceName="campaign"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <Dialog
        open={Boolean(activationTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setActivationTarget(null);
            setActivationOutboundNumberId('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select outbound number</DialogTitle>
            <DialogDescription>
              Choose a caller ID for this activation. If none is selected, campaign and
              configuration defaults will be used.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {(() => {
              const cfg = configurations.find(
                (c) => c.id === activationTarget?.configuration_id,
              ) as { call_provider?: string } | undefined;
              const useTelnyx = cfg?.call_provider === 'telnyx';
              const numbers = useTelnyx ? telnyxNumbers : twilioNumbers;
              return (
                <FormField
                  label={useTelnyx ? 'Outbound Telnyx number' : 'Outbound Twilio number'}
                  htmlFor="campaign-outbound-number"
                >
                  <FormSelect
                    id="campaign-outbound-number"
                    value={activationOutboundNumberId}
                    onChange={(event) => setActivationOutboundNumberId(event.target.value)}
                  >
                    <option value="">Use configuration/global default</option>
                    {numbers.map((number) => (
                      <option key={number.id} value={number.id}>
                        {number.friendly_name ? `${number.friendly_name} - ` : ''}
                        {number.phone_number}
                      </option>
                    ))}
                  </FormSelect>
                </FormField>
              );
            })()}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActivationTarget(null);
                setActivationOutboundNumberId('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmActivate}
              disabled={
                !activationTarget ||
                (updatingCampaignId !== null && updatingCampaignId === activationTarget.id)
              }
            >
              {updatingCampaignId !== null && updatingCampaignId === activationTarget?.id
                ? 'Activating...'
                : 'Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
